import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using EVENTSHOWCASE ("the Platform", "we", "us", or "our") at eventshowcase.de, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Platform.

We reserve the right to update these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the revised Terms. We will notify registered users of material changes via email or an in-app notice.

These Terms apply to all visitors, users, event organizers, and others who access or use the Platform.`,
  },
  {
    id: 'description',
    title: '2. Description of Service',
    content: `EVENTSHOWCASE is an event discovery and management platform that allows:

**Event Attendees** to discover, browse, and register for free and paid events hosted by organizers worldwide.

**Event Organizers** to create, publish, and manage events; collect registrations; run email campaigns; and sell digital products through their personal store.

**Social Growth Features** including social media follow gates that require attendees to follow organizer social accounts before receiving event access.

We reserve the right to modify, suspend, or discontinue any part of the Service at any time without prior notice.`,
  },
  {
    id: 'accounts',
    title: '3. User Accounts',
    content: `**Registration**
To access certain features, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.

**Account Security**
You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately at support@eventshowcase.de if you suspect unauthorized use of your account.

**Account Termination**
We reserve the right to suspend or terminate your account at our sole discretion if you violate these Terms, engage in fraudulent activity, or for any other reason we deem appropriate.

**Age Requirement**
You must be at least 13 years old to create an account. By registering, you confirm that you meet this requirement.`,
  },
  {
    id: 'organizer-terms',
    title: '4. Organizer Terms',
    content: `**Event Creation**
Organizers are solely responsible for the accuracy, legality, and quality of their events. You must not create events that are fraudulent, misleading, illegal, or violate any third-party rights.

**Commission & Fees**
EVENTSHOWCASE charges a commission on paid event ticket sales. The applicable commission rate depends on your subscription plan (Starter, Growth, or Pro). Commission rates are displayed on the pricing page and in your dashboard.

**Payouts**
Earnings are subject to a holding period before withdrawal. Withdrawal requests are processed via Paystack, Flutterwave, or PayPal depending on your region. We reserve the right to withhold payouts if we suspect fraudulent activity.

**Social Follow Gates**
Organizers may require attendees to follow their social media accounts as a condition of event registration. You are responsible for ensuring this practice complies with the terms of service of the relevant social media platforms.

**Email Campaigns**
Organizers may send promotional emails to their registered attendees. You must comply with applicable anti-spam laws (including CAN-SPAM and GDPR). Sending unsolicited bulk emails is strictly prohibited.

**Store Products**
Organizers may sell digital products through their store. You are responsible for ensuring your products do not infringe on any intellectual property rights and comply with all applicable laws.`,
  },
  {
    id: 'attendee-terms',
    title: '5. Attendee Terms',
    content: `**Registration & Tickets**
When you register for an event, you receive a confirmation and QR code ticket. Tickets are non-transferable unless the organizer explicitly permits transfers.

**Refunds**
Refund eligibility is determined by the organizer's refund policy and our platform Refund Policy. Please review the event's refund terms before registering. EVENTSHOWCASE is not responsible for refunds on behalf of organizers.

**Social Follow Requirements**
Some events require you to follow the organizer's social media accounts. By registering for such events, you agree to this requirement. You may unfollow after receiving your ticket, but the organizer may revoke access at their discretion.

**Conduct**
You agree not to misuse the Platform, attempt to circumvent social follow gates, share tickets with unauthorized parties, or engage in any activity that disrupts the Platform or other users.`,
  },
  {
    id: 'subscriptions',
    title: '6. Subscriptions & Billing',
    content: `**Plans**
EVENTSHOWCASE offers subscription plans (Starter, Growth, Pro) with different features and commission rates. Plan details are available on the pricing page.

**Billing**
Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law or as stated in our Refund Policy.

**Cancellation**
You may cancel your subscription at any time from your dashboard. Cancellation takes effect at the end of the current billing period. You will retain access to paid features until the period ends.

**Price Changes**
We reserve the right to change subscription prices with 30 days' notice. Continued use after the price change takes effect constitutes acceptance of the new pricing.`,
  },
  {
    id: 'intellectual-property',
    title: '7. Intellectual Property',
    content: `**Our Content**
The Platform and its original content, features, and functionality are owned by EVENTSHOWCASE and are protected by international copyright, trademark, and other intellectual property laws.

**Your Content**
By posting content (event descriptions, images, store products, etc.) on the Platform, you grant EVENTSHOWCASE a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content solely for the purpose of operating and promoting the Platform.

**Restrictions**
You may not copy, modify, distribute, sell, or lease any part of our Platform or its content without our prior written consent. You may not reverse engineer or attempt to extract the source code of our software.`,
  },
  {
    id: 'prohibited',
    title: '8. Prohibited Activities',
    content: `You agree not to engage in any of the following:

- Creating fake or fraudulent events to collect registrations or payments
- Impersonating any person or entity or misrepresenting your affiliation
- Using the Platform to distribute malware, spam, or harmful content
- Attempting to gain unauthorized access to any part of the Platform
- Scraping, crawling, or data-mining the Platform without our written consent
- Using automated tools to create accounts, register for events, or manipulate social follow counts
- Violating any applicable local, national, or international law or regulation
- Harassing, threatening, or intimidating other users or organizers
- Circumventing or attempting to circumvent any security or access control measures`,
  },
  {
    id: 'privacy',
    title: '9. Privacy & Cookies',
    content: `Your use of the Platform is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Platform, you consent to the collection and use of your information as described in our Privacy Policy.

We use cookies and similar tracking technologies, including Google AdSense cookies for advertising purposes. You can manage your cookie preferences through the cookie consent banner displayed on your first visit.

Please review our Privacy Policy at eventshowcase.de/legal/privacy-policy for full details on how we handle your data.`,
  },
  {
    id: 'disclaimers',
    title: '10. Disclaimers & Limitation of Liability',
    content: `**Disclaimer of Warranties**
The Platform is provided on an "AS IS" and "AS AVAILABLE" basis without any warranties of any kind, either express or implied. We do not warrant that the Platform will be uninterrupted, error-free, or free of viruses or other harmful components.

**Limitation of Liability**
To the maximum extent permitted by applicable law, EVENTSHOWCASE shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or goodwill, arising out of or in connection with your use of the Platform.

**Organizer Responsibility**
EVENTSHOWCASE is a platform provider and is not responsible for the actions, content, or events of organizers. We do not guarantee the quality, safety, or legality of any event listed on the Platform.

**Maximum Liability**
Our total liability to you for any claims arising from your use of the Platform shall not exceed the amount you paid to us in the 12 months preceding the claim.`,
  },
  {
    id: 'indemnification',
    title: '11. Indemnification',
    content: `You agree to defend, indemnify, and hold harmless EVENTSHOWCASE and its officers, directors, employees, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Platform, including but not limited to your content, your events, your products, or your violation of any third-party rights.`,
  },
  {
    id: 'governing-law',
    title: '12. Governing Law & Disputes',
    content: `These Terms shall be governed by and construed in accordance with the laws of Germany, without regard to its conflict of law provisions.

Any disputes arising out of or relating to these Terms or the Platform shall first be attempted to be resolved through good-faith negotiation. If negotiation fails, disputes shall be submitted to the competent courts of Germany.

If you are an EU consumer, you may also use the European Commission's Online Dispute Resolution platform at https://ec.europa.eu/consumers/odr.`,
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    content: `If you have any questions about these Terms of Service, please contact us:

**EVENTSHOWCASE**
Website: https://eventshowcase.de
Email: legal@eventshowcase.de

For urgent legal matters, please include "LEGAL" in the subject line of your email.`,
  },
];

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    const PAGE_TITLE = 'Terms of Service – EVENTSHOWCASE';
    const PAGE_DESC = 'Read the EVENTSHOWCASE Terms of Service. Understand your rights and responsibilities as an attendee, event organizer, or visitor on our platform.';
    const PAGE_URL = `${SITE_URL}/legal/terms`;

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [a, v] = sel.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(a.trim(), v.replace(/"/g, '').trim());
        document.head.appendChild(el);
        el.setAttribute('data-page-meta', 'true');
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[name="robots"]', 'content', 'index, follow');
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service – EVENTSHOWCASE',
      description: PAGE_DESC,
      url: PAGE_URL,
      dateModified: '2026-04-05',
      isPartOf: { '@type': 'WebSite', name: 'EVENTSHOWCASE', url: SITE_URL },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Legal', item: `${SITE_URL}/legal` },
          { '@type': 'ListItem', position: 3, name: 'Terms of Service', item: PAGE_URL },
        ],
      },
    };

    const existing = document.getElementById('schema-terms');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-terms';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-terms');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  const renderContent = (content: string) => {
    return content.split('\n\n').map((paragraph, i) => {
      const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-gray-600 text-sm leading-relaxed mb-3">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} className="text-gray-800 font-semibold">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/35af317c-ff06-4831-b241-787dd1236a773.png"
              alt="EVENTSHOWCASE"
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <Link to="/legal/privacy-policy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
            <Link to="/legal/refund-policy" className="hover:text-gray-900 transition-colors">Refund Policy</Link>
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gray-950 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-medium px-3 py-1.5 rounded-full mb-6">
            <i className="ri-file-text-line text-sm"></i>
            Legal Document
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4 font-['Playfair_Display']">Terms of Service</h1>
          <p className="text-gray-400 text-sm sm:text-base max-w-2xl">
            Please read these Terms carefully before using EVENTSHOWCASE. They govern your use of the platform as an attendee, organizer, or visitor.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Last Updated: April 5, 2026
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-global-line"></i>
              Applies to: eventshowcase.de
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-scales-3-line"></i>
              Governed by German Law
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <div className="flex flex-col lg:flex-row gap-10">

          {/* Table of Contents */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={() => setActiveSection(section.id)}
                    className={`block text-xs py-1.5 px-3 rounded-md transition-colors cursor-pointer ${
                      activeSection === section.id
                        ? 'bg-gray-900 text-white font-medium'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {section.title}
                  </a>
                ))}
              </nav>

              <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-xs font-semibold text-gray-700 mb-2">Related Policies</p>
                <div className="space-y-2">
                  <Link
                    to="/legal/privacy-policy"
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-shield-check-line text-xs"></i>
                    Privacy Policy
                  </Link>
                  <Link
                    to="/legal/refund-policy"
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-refund-2-line text-xs"></i>
                    Refund &amp; Tax Policy
                  </Link>
                  <Link
                    to="/subscribe"
                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    <i className="ri-price-tag-3-line text-xs"></i>
                    Pricing Plans
                  </Link>
                </div>
              </div>
            </div>
          </aside>

          {/* Policy Sections */}
          <main className="flex-1 min-w-0">
            <div className="space-y-10">
              {sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-24">
                  <h2 className="text-base font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">
                    {section.title}
                  </h2>
                  <div>{renderContent(section.content)}</div>
                </section>
              ))}
            </div>

            {/* Footer note */}
            <div className="mt-12 p-6 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-gray-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">Questions about these Terms?</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Contact us at{' '}
                    <a href="mailto:legal@eventshowcase.de" className="text-gray-800 underline hover:no-underline">
                      legal@eventshowcase.de
                    </a>. We aim to respond within 5 business days.
                  </p>
                </div>
              </div>
            </div>

            {/* Related policies */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link
                to="/legal/privacy-policy"
                className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors flex-shrink-0">
                  <i className="ri-shield-check-line text-gray-600"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800">Privacy Policy</p>
                  <p className="text-xs text-gray-400">Data &amp; cookies</p>
                </div>
              </Link>
              <Link
                to="/legal/refund-policy"
                className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors flex-shrink-0">
                  <i className="ri-refund-2-line text-gray-600"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800">Refund Policy</p>
                  <p className="text-xs text-gray-400">Payment terms</p>
                </div>
              </Link>
              <Link
                to="/"
                className="flex items-center gap-3 p-4 border border-gray-100 rounded-xl hover:border-gray-300 transition-colors cursor-pointer group"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors flex-shrink-0">
                  <i className="ri-home-4-line text-gray-600"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800">Back to Home</p>
                  <p className="text-xs text-gray-400">EVENTSHOWCASE</p>
                </div>
              </Link>
            </div>
          </main>
        </div>
      </div>

      {/* Footer strip */}
      <div className="border-t border-gray-100 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <p>&copy; 2026 EVENTSHOWCASE. All rights reserved.</p>
          <div className="flex items-center gap-3">
            <Link to="/legal/terms" className="text-gray-700 font-medium">Terms of Service</Link>
            <span>·</span>
            <Link to="/legal/privacy-policy" className="hover:text-gray-700 transition-colors">Privacy Policy</Link>
            <span>·</span>
            <Link to="/legal/refund-policy" className="hover:text-gray-700 transition-colors">Refund Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
